import { Space, Tag, Button, Tooltip, Modal, Card, Typography, Badge, Dropdown, Switch, Divider } from 'antd';
import type { MenuProps } from 'antd';
import { QuestionCircleOutlined, SearchOutlined, BookOutlined, CodeOutlined, CreditCardOutlined, ExperimentOutlined, CommentOutlined, BugOutlined, BulbOutlined, RobotOutlined, GiftOutlined, EyeOutlined, SyncOutlined, TeamOutlined, FlagOutlined } from '@ant-design/icons';
import { ProCard } from '@ant-design/pro-components';
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from '@/hooks/useLocation';
import { useModel } from '@/context/AuthContext';
import { useCommandPaletteContext } from '@/context/CommandPaletteContext';
import { useAIColleaguesContext } from '@/context/AIColleaguesContext';
import { useTabNavigationSafe } from '@/context/TabsContext';
import { usePermissionOverlay } from '@/context/PermissionOverlayContext';
import { history } from '@/utils/navigation';
import Notification from '../Notifications';
import Avatar from './AvatarDropdown';
import { MegaMenuButton } from '@/components/MegaMenu';
import { isPermitted, ModuleName, PermissionVerbAction } from '@/components/Guard';
import styles from './index.module.less';

const { Text } = Typography;

export type SiderTheme = 'light' | 'dark';

const ENVTagColor = {
  dev: 'orange',
  test: 'green',
  pre: '#87d068'
};

/**
 * Search Trigger Component
 * Looks like a search input but opens Command Palette on click
 */
const SearchTrigger: React.FC = () => {
  const { open } = useCommandPaletteContext();

  // Detect OS for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  return (
    <div className={styles.searchTrigger} onClick={open} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && open()}>
      <SearchOutlined className={styles.searchTriggerIcon} />
      <span className={styles.searchTriggerPlaceholder}>Search users, instructions, cards...</span>
      <span className={styles.searchTriggerShortcut}>
        <kbd>{shortcutKey}</kbd>
        <kbd>K</kbd>
      </span>
    </div>
  );
};

/**
 * AI Assistant Button
 * Opens the AI Colleagues modal
 */
const AIAssistantButton: React.FC = () => {
  const { openAIColleagues } = useAIColleaguesContext();

  // Detect OS for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  return (
    <Tooltip title={`Digital Staff (${shortcutKey}+J)`}>
      <Button
        type="text"
        aria-label="Digital Staff"
        data-testid="digital-staff-open-button"
        icon={<RobotOutlined style={{ fontSize: '18px' }} />}
        onClick={() => openAIColleagues()}
        className={styles.iconButton}
      />
    </Tooltip>
  );
};

/**
 * What's New Button
 * Links to the release notes and changelog
 */
const WhatsNewButton: React.FC = () => {
  return (
    <Tooltip 
      title={
        <div>
          <div style={{ fontWeight: 600 }}>What's New</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>Release notes, updates, and changelog</div>
        </div>
      }
    >
      <Button
        type="text"
        icon={<GiftOutlined style={{ fontSize: '18px' }} />}
        className={styles.iconButton}
        onClick={() => history.push('/whats-new')}
      />
    </Tooltip>
  );
};

/**
 * Operator Manual Button
 * Links to the operator manual/documentation
 */
const OperatorManualButton: React.FC = () => {
  return (
    <Tooltip 
      title={
        <div>
          <div style={{ fontWeight: 600 }}>Operators Manual</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>Guides and documentation for using the portal</div>
        </div>
      }
    >
      <Button
        type="text"
        icon={<BookOutlined style={{ fontSize: '18px' }} />}
        className={styles.iconButton}
        onClick={() => history.push('/manual')}
      />
    </Tooltip>
  );
};

/**
 * Developer Console menu items
 */
interface DevMenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  description?: string;
  requiresFeatureFlagOutputOverrides?: boolean;
}

const devMenuItems: DevMenuItem[] = [
  {
    key: 'digital-deposit-sim',
    label: 'Digital Deposit Simulation',
    icon: <ExperimentOutlined />,
    path: '/developer-console/digital-deposit-simulation',
    description: 'Simulate Fireblocks digital deposit events'
  },
  {
    key: 'remarks-testing',
    label: 'Remarks Testing',
    icon: <CommentOutlined />,
    path: '/developer-console/remarks-testing',
    description: 'Test OperatorRemark component with AI features'
  },
  {
    key: 'card-transaction-sim',
    label: 'Card Transaction Simulation',
    icon: <CodeOutlined />,
    path: '/developer-console/card-transaction-simulation',
    description: 'Simulate card transaction lifecycle events'
  },
  {
    key: 'card-renewal-sim',
    label: 'Card Renewal Simulation',
    icon: <SyncOutlined />,
    path: '/developer-console/card-renewal-simulation',
    description: 'Simulate card renewal case creation'
  },
  {
    key: 'generate-unissued-cards',
    label: 'Generate Unissued Cards',
    icon: <CreditCardOutlined />,
    path: '/developer-console/generate-unissued-cards',
    description: 'Generate unissued card records for a card product'
  },
  {
    key: 'feature-flag-output-overrides',
    label: 'Feature Flag Output Overrides',
    icon: <FlagOutlined />,
    path: '/developer-console/feature-flag-output-overrides',
    description: 'Override object feature flag outputs for one user in non-production',
    requiresFeatureFlagOutputOverrides: true
  }
];

/**
 * Developer Console Button
 * Opens the developer console modal for dev tools
 */
const DevConsoleButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const env = (window as any).env;
  const { initialState } = useModel('@@initialState');
  const { enabled: overlayEnabled, showUnauthorized, setEnabled, setShowUnauthorized } = usePermissionOverlay();

  // Check if dev console should be shown
  const shouldShow = process.env.NODE_ENV === 'development' || env?.ENABLE_DEVELOPER_CONSOLE === 'true';
  const featureFlagOutputOverridesEnabled =
    env?.ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES === 'true' || env?.ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES === '1';
  const canEditFeatureFlagOverrides = isPermitted(
    ModuleName.FEATURE_FLAG_RULES,
    PermissionVerbAction['ACTION:EDIT_FEATURE_FLAG_RULES'],
    initialState?.currentUser?.acl
  );
  const visibleDevMenuItems = devMenuItems.filter(
    (item) => !item.requiresFeatureFlagOutputOverrides || (featureFlagOutputOverridesEnabled && canEditFeatureFlagOverrides)
  );

  // Check if current path is a developer console route
  const isDevConsolePage = location.pathname.startsWith('/developer-console');

  // Detect OS for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  const handleMenuClick = (path: string) => {
    history.push(path);
    setOpen(false);
  };

  // Keyboard shortcut: Ctrl/Cmd + D to toggle modal
  useEffect(() => {
    if (!shouldShow) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <Tooltip title={`Developer Console (${shortcutKey}+D)`}>
        <Badge dot={isDevConsolePage || overlayEnabled} offset={[-4, 4]}>
          <Button
            type="text"
            icon={<CodeOutlined style={{ fontSize: '18px' }} />}
            onClick={() => setOpen(true)}
            className={styles.iconButton}
          />
        </Badge>
      </Tooltip>
      <Modal
        title="Developer Console"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={600}
        maskClosable={false}
        keyboard
        destroyOnClose
      >
        <ProCard type="inner">
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Developer tools for testing and simulation in non-production environments.
          </Text>

          {/* Permission Overlay Section */}
          <Card
            style={{
              marginBottom: 12,
              borderLeft: overlayEnabled ? '3px solid #52c41a' : undefined
            }}
          >
            <Card.Meta
              avatar={<div style={{ fontSize: 32, color: overlayEnabled ? '#52c41a' : '#1890ff' }}><EyeOutlined /></div>}
              title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Permission Overlay</span>
                  <Switch
                    checked={overlayEnabled}
                    onChange={setEnabled}
                    size="small"
                  />
                </div>
              }
              description={
                <div>
                  <div>Visualize which UI elements are protected by permissions</div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Shortcut: {shortcutKey}+Shift+P
                  </Text>
                  {overlayEnabled && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Switch
                        checked={showUnauthorized}
                        onChange={setShowUnauthorized}
                        size="small"
                      />
                      <Text style={{ fontSize: 12 }}>Show unauthorized elements (dimmed)</Text>
                    </div>
                  )}
                </div>
              }
            />
          </Card>

          <Divider style={{ margin: '16px 0' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Simulation Tools</Text>
          </Divider>

          {visibleDevMenuItems.map((item) => (
            <Card
              key={item.path}
              hoverable
              onClick={() => handleMenuClick(item.path)}
              style={{
                marginBottom: 12,
                borderLeft: location.pathname === item.path ? '3px solid #1890ff' : undefined
              }}
            >
              <Card.Meta
                avatar={<div style={{ fontSize: 32, color: '#1890ff' }}>{item.icon}</div>}
                title={item.label}
                description={item.description}
              />
            </Card>
          ))}
        </ProCard>
      </Modal>
    </>
  );
};

/**
 * Header Support Button
 * Dropdown menu with options to report issue, give feedback, or request feature
 */
const HeaderSupportButton: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const { openTab } = useTabNavigationSafe();

  // Check if should hide based on enabled vendors
  const shouldHide = !initialState?.supportCenterEnabledVendors || initialState.supportCenterEnabledVendors.length === 0;

  const handleNavigate = useCallback((path: string, title: string) => {
    openTab(path, title);
  }, [openTab]);

  // Keyboard shortcut: Ctrl+Alt+I opens landing page
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.altKey && (event.key === 'i' || event.key === 'I')) {
        event.preventDefault();
        handleNavigate('/support-center/new', 'Support Center');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNavigate]);

  const menuItems: MenuProps['items'] = [
    {
      key: 'report-issue',
      icon: <BugOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Report Issue',
      onClick: () => handleNavigate('/support-center/report-issue', 'Report Issue')
    },
    {
      key: 'client-issue',
      icon: <TeamOutlined style={{ color: '#faad14' }} />,
      label: 'Client Issue',
      onClick: () => handleNavigate('/support-center/client-issue', 'Client Issue')
    },
    {
      key: 'feedback',
      icon: <CommentOutlined style={{ color: '#1890ff' }} />,
      label: 'Give Feedback',
      onClick: () => handleNavigate('/support-center/feedback', 'Give Feedback')
    },
    {
      key: 'feature-request',
      icon: <BulbOutlined style={{ color: '#52c41a' }} />,
      label: 'Feature Request',
      onClick: () => handleNavigate('/support-center/feature-request', 'Feature Request')
    },
    { type: 'divider' },
    {
      key: 'all-options',
      icon: <QuestionCircleOutlined />,
      label: 'All Support Options',
      onClick: () => handleNavigate('/support-center/new', 'Support Center')
    }
  ];

  if (shouldHide) {
    return null;
  }

  return (
    <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
      <Tooltip title="Get Support (Ctrl+Alt+I)">
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: '18px' }} />}
          className={styles.iconButton}
        />
      </Tooltip>
    </Dropdown>
  );
};

const GlobalHeaderRight: React.FC<{}> = () => {
  const { initialState } = useModel('@@initialState');

  if (!initialState || !initialState.settings) {
    return null;
  }

  const { navTheme, layout } = initialState.settings;
  let className = styles.right;

  if ((navTheme === 'dark' && layout === 'top') || layout === 'top') {
    className = `${styles.right}  ${styles.dark}`;
  }

  return (
    <div className={styles.globalHeaderRight}>
      <MegaMenuButton />
      <SearchTrigger />
      <Space className={className} style={{ width: 'fit-content' }}>
        <AIAssistantButton />
        <DevConsoleButton />
        <WhatsNewButton />
        <OperatorManualButton />
        <HeaderSupportButton />
        <Notification />
        <Avatar menu />
        {REACT_APP_ENV && (
          <span>
            <Tag color={ENVTagColor[REACT_APP_ENV]}>{REACT_APP_ENV}</Tag>
          </span>
        )}
      </Space>
    </div>
  );
};
export default GlobalHeaderRight;
